/**
 * `<lucent-insight>` Web Component.
 *
 * Bundles `EmbedHost.vue` as a custom element so any host page (the
 * dashboard, a customer's product, a static demo) can drop a single
 * tag in and get an interactive insight without a build step:
 *
 *     <script type="module" src="https://insights-api.host/runtime.js"></script>
 *     <lucent-insight
 *       api-url="https://insights-api.host"
 *       token="<insight-scoped JWT>"
 *       insight-id="ins_abc">
 *     </lucent-insight>
 *
 * Attribute → prop mapping is automatic (Vue's `defineCustomElement`
 * handles kebab-case → camelCase). The element exposes two state-
 * mutation methods so embedders can drive filters from JS:
 *
 *   - `setState(partial)` — SHALLOW merges `partial` into the
 *     `host` half of the namespaced viewer state. `undefined`
 *     deletes a key; `null` writes null.
 *   - `replaceState(state)` — replaces the `host` half wholesale.
 *
 * Multiple state mutations within one animation frame coalesce
 * into a single rerun.
 *
 * Two additional attributes/props seed initial state:
 *
 *   - `initial-state` / `initialState` — the `host` half.
 *   - `initial-author-state` / `initialAuthorState` — the `author`
 *     half (values that would normally come from `sbi.state(...)`
 *     inside the worker). Honoured **only when the embed page is
 *     same-origin with `api-url`**; cross-origin pages have it
 *     silently dropped to prevent forged drilldown state on first
 *     paint. The dashboard's PDF print template runs same-origin
 *     under headless Chromium so the gate lets it through.
 *
 * ## Event surface
 *
 * `defineCustomElement` forwards every Vue-emitted event from
 * `EmbedHost.vue` as a DOM `CustomEvent` on this element with the
 * same name. The contract below pins the names + detail shapes
 * embedders depend on:
 *
 * **Lifecycle**
 *   - `lucent-ready` — fires once the first render lands. Detail is `null`.
 *   - `lucent-print-ready` — print-mode-only. Fires once when the first
 *     render lands AND every dispatched query has resolved. The
 *     headless PDF renderer waits on this signal before capturing.
 *     Detail is `null`.
 *   - `state-change` — fires once per **committed** viewer-state
 *     mutation (filter / select / date / drilldown). Detail is the
 *     namespaced `{host, author}` envelope. Coalesced or cancelled
 *     mutations do NOT emit, so the payload always matches what
 *     the embed actually rendered. The dashboard mirrors this so
 *     the PDF export route can forward both halves as
 *     `initialState` / `initialAuthorState` for a same-origin
 *     round-trip. **DOM contract v2** — the previous flat-merged
 *     shape is removed. Consumers reading flat keys should now
 *     read `detail.host[k]` or `detail.author[k]` explicitly.
 *   - `rerun-start` — fires on every new pass after first render.
 *     Pairs with `rerun-end` so embedders can flip `aria-busy`
 *     symmetrically. Detail is `null`.
 *   - `rerun-end` — fires when the current pass terminates.
 *     Detail: `{status: "committed" | "errored"}`. Cancelled
 *     passes do NOT emit `rerun-end` on the cancel; the
 *     superseding pass fires its own.
 *
 * **Errors (8-code taxonomy)**
 *   - `lucent-error` — fatal error preventing the chart from rendering.
 *     Detail: `LucentErrorDetail`. Embedders style the migration banner
 *     off `code` (e.g. branded "this insight needs updating" UI for
 *     `code === "sbi_chart_removed"`).
 *
 *   The 8 codes:
 *   - `sbi_chart_removed`     — saved insight uses legacy `sbi.chart(...)`.
 *   - `chart_type_renamed`    — saved insight uses `type: "box"` (now `boxplot`).
 *   - `unknown_chart_type`    — `sbi.viz.<unknown>` (typo / unsupported).
 *   - `chart_spec_invalid`    — Ajv validation failed; `instancePath` set.
 *   - `chart_data_too_large`  — per-type row cap exceeded.
 *   - `sankey_cycle_detected` — informational; resolved per `onCycle`.
 *   - `sunburst_depth_exceeded` — depth > 16 levels.
 *   - `branch_test_failed`    — preserved from v1 branch plan; consistent taxonomy.
 *
 * **Interactivity**
 *   - `lucent-click` — chart click. Detail: `LucentClickDetail`. The
 *     `datum` shape varies per chart type (see briefing's click-payload
 *     reference table). Authors register handlers via `sbi.onClick(id, fn)`
 *     in insight code; this DOM event is the host-page-side equivalent
 *     for embedders that want to drive their own routing.
 *
 * **Diagnostics (non-fatal)**
 *   - `lucent-diagnostic` — informational warnings that don't prevent
 *     rendering. Detail: `LucentDiagnosticDetail`. Coalesced to ≤ 1 per
 *     code per render pass with `count: N` (round-3 Integrator dedupe
 *     contract — prevents firehose on a 2000-edge sankey with cycles).
 *
 * Codes used for diagnostics include `sankey_cycle_detected`,
 * `sankey_cycle_self_looped`, `sankey_cycle_dropped`,
 * `sunburst_depth_exceeded`, `sunburst_orphan_rows`,
 * `lazy_in_eager_wasted_query`. Diagnostics share names with errors
 * when the same situation can be either soft-warn or fatal depending
 * on configuration.
 *
 * **UI telemetry**
 *   - `accordion-toggle` — fires on every `sbi.ui.accordion(...)`
 *     `<details>` toggle. Detail: `LucentAccordionToggleDetail`.
 *     Useful for "which sections do users open?" analytics.
 *   - `callout-rendered` — fires once per Vue mount of a
 *     `sbi.ui.callout(...)`. Detail: `LucentCalloutRenderedDetail`.
 *     Useful for badging host-page sidebars with danger-callout counts.
 *     Not coalesced — integrators debounce client-side if needed.
 *
 * ## Chrome toolbar + fullscreen
 *
 * Every embed renders a thin chrome strip above the insight body —
 * a tab list on the left (always shows "Insight"; "Queries" /
 * "Versions" / "Code" appear when the embed-token row opted in) and
 * an icon-only fullscreen button on the right. Click promotes the
 * `<lucent-insight>` element via the standard Fullscreen API;
 * Escape exits natively.
 *
 * **Iframe embedders:** the parent iframe must carry the
 * `allowfullscreen` attribute (or `allow="fullscreen"`) for the
 * promotion to succeed. Without it, the browser rejects the
 * `requestFullscreen()` call and the embed emits a `lucent-error`
 * with `code: "fullscreen_unavailable"`. The embed itself keeps
 * rendering normally — the failure is soft.
 *
 * ## CSS custom properties (embedder-facing)
 *
 * - `--lucent-font-scale` (default `1`) — multiplier applied to
 *   the embed's internal type sizes (currently KPI value sizes;
 *   chart axis / label scaling is on the follow-up list). Auto-
 *   bumped to `1.4` inside `:host(:fullscreen)` so boardroom
 *   projection at ~4 m sightline lands the support KPIs in
 *   legible range. Embedders can override on the host element to
 *   force an always-large embed:
 *
 *       <lucent-insight style="--lucent-font-scale: 1.6" …>
 *
 *   The variable cascades through the shadow root; nothing in the
 *   insight author surface reads or sets it.
 */

import { defineCustomElement } from "vue";
import EmbedHost from "./components/EmbedHost.vue";

/**
 * `lucent-error.detail.code` taxonomy. Stable contract with embedders —
 * names won't change without a deprecation cycle.
 *
 * Eight chart-specific codes (round-2 plan #2) plus the pre-v1 codes
 * the runner has always emitted (`runtime_error`, `token_expired`,
 * `fetch_failed`, `malformed_response`). Embedders narrow on `code`
 * to route migration banners, retry flows, and observability.
 */
export type LucentErrorCode =
  // Chart taxonomy (round-2)
  | "sbi_chart_removed"
  | "chart_type_renamed"
  | "unknown_chart_type"
  | "chart_spec_invalid"
  | "chart_data_too_large"
  | "sankey_cycle_detected"
  | "sunburst_depth_exceeded"
  | "branch_test_failed"
  // Pre-v1 codes (still emitted by the runner)
  | "runtime_error"
  | "token_expired"
  | "fetch_failed"
  | "malformed_response"
  // Publish gate — emitted when a service-key-minted token tries to
  // render an insight whose `is_published` flag is false. Same code
  // surfaces on both mint-time (POST /api/tenant/embed-tokens) and
  // read-time (GET /api/embed/insights/:id) — integrators decide
  // whether to surface "ask the owner to publish" or treat the embed
  // as permanently unavailable. Plan §10.
  | "INSIGHT_NOT_PUBLISHED"
  // Chrome / host-side codes
  | "fullscreen_unavailable";

/** Shape of `event.detail` on `lucent-error` events. */
export interface LucentErrorDetail {
  code: LucentErrorCode | string;
  /** The insight currently rendering. */
  insightId?: string;
  /** Human-readable message safe to surface to viewers. */
  message: string;
  /**
   * For `sbi_chart_removed` / `chart_type_renamed` — the recommended
   * replacement call shape (e.g. `"sbi.viz.line"`). Embedders include
   * this verbatim in the migration banner so the author / admin sees
   * the exact fix.
   */
  suggestedReplacement?: string;
  /** Original call source if available — useful for support tickets. */
  originalCall?: string;
  /** Ajv `instancePath` for `chart_spec_invalid` (e.g. "/spec/series/0/yAxis"). */
  instancePath?: string;
}

/**
 * Discriminated `lucent-click.detail.datum` shapes per chart type. The
 * dispatcher and the per-chart `<v-chart>` components emit their type
 * verbatim; embedders narrow off `chartType`.
 */
export type LucentClickDatum =
  | { kind: "node"; name: string }
  | { kind: "link"; source: string; target: string; value: number }
  | { path: string[]; name: string; value: number }
  | Record<string, unknown>;

/** Shape of `event.detail` on `lucent-click` events. */
export interface LucentClickDetail {
  insightId?: string;
  /** The chart type that fired the click — narrows `datum` consumer-side. */
  chartType?: string;
  /** Author-supplied `id` on the chart spec (or auto-generated). */
  targetId?: string;
  datum: LucentClickDatum;
}

/** Shape of `event.detail` on `lucent-diagnostic` events. */
export interface LucentDiagnosticDetail {
  code: string;
  insightId?: string;
  /** When ≥ 2, the host coalesced N events of the same code into one. */
  count?: number;
  /** Per-code structured payload (cycle source/target, depth, etc.). */
  detail?: Record<string, unknown>;
}

/** Shape of `event.detail` on `accordion-toggle` events. */
export interface LucentAccordionToggleDetail {
  insightId?: string;
  /** The accordion's summary text (matches the author's `summary` arg). */
  summary: string;
  /** New open state — `true` when the accordion just expanded. */
  open: boolean;
}

/** Shape of `event.detail` on `callout-rendered` events. */
export interface LucentCalloutRenderedDetail {
  insightId?: string;
  /** One of `"info" | "success" | "warn" | "danger"`. */
  kind: string;
  /** Author-supplied `opts.title`, when set. */
  title?: string;
}

/**
 * Custom-element wrapper around `EmbedHost`.
 *
 * `defineCustomElement` reads `<script setup>` props from the SFC and
 * exposes them as element attributes (kebab-cased). It also forwards
 * Vue-emitted events as DOM `CustomEvent`s on the host element with
 * the same name — which is why we re-emit using the `lucent-`
 * prefix below: a bare `error` event would clash with the standard
 * DOM error event.
 */
const LucentInsightElement = defineCustomElement(EmbedHost, {
  // Render into shadow DOM so the host page's CSS doesn't bleed in.
  // Components inside the embed bundle their own scoped styles
  // (`<style scoped>` in the SFCs); the shadow root keeps them out
  // of the page CSS scope and vice versa.
  shadowRoot: true,
});

/**
 * Register the element.
 *
 * Idempotent: re-running this in the same window (e.g. dev hot-reload
 * loading the bundle twice) is a no-op rather than an exception. We
 * also bail out cleanly when running in non-DOM contexts (Node /
 * vitest happy-dom in pre-mount setup) so importing this module
 * doesn't throw.
 */
export const registerLucentInsight = (tagName = "lucent-insight"): void => {
  if (typeof customElements === "undefined") return;
  if (customElements.get(tagName)) return;
  customElements.define(tagName, LucentInsightElement);
};

// Auto-register on import. Consumers who want a different tag name
// should import `registerLucentInsight` directly (named export below)
// and pass their own.
registerLucentInsight();

// Named export so embedders bundling via npm can import a constructor
// directly if they prefer programmatic registration.
export { LucentInsightElement };

// Default export so the IIFE bundle's `globalThis.LucentInsights`
// (configured in vite.config.ts) is the registration helper itself.
export default {
  /** Programmatic registration with a custom tag name. */
  register: registerLucentInsight,
  /** The element constructor, in case you want to extend it. */
  Element: LucentInsightElement,
};
